import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import BaseComponent from 'components/BaseComponent';
import styledComp from 'components/StyledComp';
import DataTables from 'components-desktop/DataTables';
import TfootSummary from 'components-desktop/TfootSummary';
import helpers from 'misc/helpers';

const propTypes = {
  title: PropTypes.string,

  sickLeaveReport: PropTypes.object.isRequired,
  coordinators: PropTypes.array.isRequired,
  teams: PropTypes.array.isRequired,
};

const defaultProps = {
  title: '',
};

@autobind
class TableSickLeaves extends BaseComponent {
  constructor(props) {
    super(props);
  }

  componentDidMount() {
  }

  createColumns() {
    return [{
      title: 'Id',
      dataIndex: 'caregiverId',
      sorter: (a, b) => a.caregiverId - b.caregiverId,
    }, {
      title: 'Caregiver',
      dataIndex: 'caregiverName',
      sorter: (a, b) => a.caregiverName?.localeCompare(b.caregiverName),
      defaultSortOrder: 'ascend',
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/caregivers/caregiver_profile?id=${record.caregiverId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Coordinator',
      dataIndex: 'coordinator',
      sorter: (a, b) => a.coordinator?.localeCompare(b.coordinator),
    }, {
      title: 'Team',
      dataIndex: 'team',
      sorter: (a, b) => a.team?.localeCompare(b.team),
    }, {
      title: 'Total Visit Hours',
      dataIndex: 'totalVisitHours',
      sorter: (a, b) => a.totalVisitMinutes - b.totalVisitMinutes,
    }, {
      title: 'Total Sick Leave Hours',
      dataIndex: 'availableSickLeaveHours',
      sorter: (a, b) => a.availableSickLeaveMinutes - b.availableSickLeaveMinutes,
    }, {
      title: 'Taken Sick Leave Hours',
      dataIndex: 'takenSickLeaveHours',
      sorter: (a, b) => a.takenSickLeaveMinutes - b.takenSickLeaveMinutes,
    }, {
      title: 'Remaining Sick Leave Hours',
      dataIndex: 'remainingSickLeaveHours',
      sorter: (a, b) => a.remainingSickLeaveMinutes - b.remainingSickLeaveMinutes,
    }];
  }

  createDataSource(sickLeaveReport) {
    const { coordinators, teams } = this.props;
    const rows = sickLeaveReport.getRemoteObjects('rows');

    const dataSource = [];
    rows.forEach((row, index) => {
      dataSource.push({
        key: row.getLong('caregiverId'),
        caregiverId: row.getLong('caregiverId'),
        caregiverName: row.getString('caregiverName'),
        coordinator: coordinators.find(c => c.id === row.getLong('coordinatorId'))?.name,
        team: teams.find(t => t.id === row.getLong('teamId'))?.name,
        totalVisitMinutes: row.getLong('totalVisitMinutes'),
        totalVisitHours: helpers.min2DecimalHour(row.getLong('totalVisitMinutes')),
        availableSickLeaveMinutes: row.getLong('availableSickLeaveMinutes'),
        availableSickLeaveHours: helpers.min2DecimalHour(row.getLong('availableSickLeaveMinutes')),
        takenSickLeaveMinutes: row.getLong('takenSickLeaveMinutes'),
        takenSickLeaveHours: helpers.min2DecimalHour(row.getLong('takenSickLeaveMinutes')),
        remainingSickLeaveMinutes:
          row.getLong('availableSickLeaveMinutes') - row.getLong('takenSickLeaveMinutes'),
        remainingSickLeaveHours: helpers.min2DecimalHour(
          row.getLong('availableSickLeaveMinutes') - row.getLong('takenSickLeaveMinutes')),
      });
    });
    return dataSource;
  }

  renderTfoot(columnsToDisplay, dataRowsToDisplay) {
    return (
      <TfootSummary
        columnsToDisplay={columnsToDisplay}
        dataRowsToDisplay={dataRowsToDisplay}
        isTableSelectable={false}
        columnKeysToAlign={[
          'totalVisitHours',
          'availableSickLeaveHours',
          'takenSickLeaveHours',
          'remainingSickLeaveHours',
        ]}
        reducers={[
          TfootSummary.SUM_REDUCER_GENERATOR('totalVisitMinutes'),
          TfootSummary.SUM_REDUCER_GENERATOR('availableSickLeaveMinutes'),
          TfootSummary.SUM_REDUCER_GENERATOR('takenSickLeaveMinutes'),
          TfootSummary.SUM_REDUCER_GENERATOR('remainingSickLeaveMinutes')]
        }
        formatters={[
          n => helpers.min2DecimalHour(n),
          n => helpers.min2DecimalHour(n),
          n => helpers.min2DecimalHour(n),
          n => helpers.min2DecimalHour(n),
        ]}
      />
    );
  }

  renderContent() {
    const { sickLeaveReport, title, ...layoutProps } = this.props;

    return (
      <DataTables
        {...layoutProps}
        color="green_haze"
        fontSize="14px"
        size="small"
        title={title}
        icon="user-add"
        selectable={false}
        columns={this.createColumns()}
        dataSource={this.createDataSource(sickLeaveReport)}
        tfoot={this.renderTfoot}
      />
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
TableSickLeaves.propTypes = propTypes;
TableSickLeaves.defaultProps = defaultProps;

export default TableSickLeaves;
